import {
  validateAndSecureRedirect,
  type ModuleAccessValidationResult
} from '../services/moduleAccessValidator';
import type { SecurityAttributeSetting } from '@invision/core';
import { ApplicationHelpers } from '@invision/core';
import { getAccessibleApplications } from '../constants/moduleSecurityAttributes.constants';

/**
 * Navigation utilities for authentication module
 * Following customercare2 patterns for URL handling
 */

/**
 * Check if a path requires cross-module navigation (full page redirect)
 * Returns true for paths that belong to other modules
 * Uses the centralized application configuration from core
 */
export function isExternalModulePath(path: string): boolean {
  // If it's an authentication path, it's internal
  if (path.startsWith('/authentication')) {
    return false;
  }

  // Check if it matches any known application base path
  const allApps = Object.values(ApplicationHelpers.allApplications);
  return allApps.some(
    (app) =>
      path.startsWith(app.basePath) ||
      (!!app.angularBasePath && path.startsWith(app.angularBasePath))
  );
}

/**
 * Resolve where a user should land after authentication, honoring the modules
 * they are actually allowed to use.
 *
 * Users with no accessible modules are reported as `no-access`; users without a
 * requested URL are sent to their first accessible module; and a requested URL
 * is validated against their access (allowed, or a fallback to an accessible
 * module).
 *
 * @param requestedRedirectUrl - The requested redirect URL from session storage
 * @param userSecurityAttributes - User's security attributes from session
 * @returns Detailed validation result with the routing outcome
 */
export function getSecurePostAuthRedirectInfo(
  requestedRedirectUrl: string | null,
  userSecurityAttributes: SecurityAttributeSetting[] = []
): ModuleAccessValidationResult {
  const accessibleApps = getAccessibleApplications(userSecurityAttributes);

  // The user cannot use any module - there is no valid destination.
  if (accessibleApps.length === 0) {
    return {
      outcome: 'no-access',
      redirectPath: '',
      hasAccess: false,
      isFallback: true
    };
  }

  // With no explicit request, default to the user's first accessible module.
  const finalRedirectUrl =
    requestedRedirectUrl ||
    ApplicationHelpers.getApplicationEntryPath(accessibleApps[0]);

  return validateAndSecureRedirect(finalRedirectUrl, userSecurityAttributes);
}
